import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Centralized namespace for all persisted filter state.
 * Keeps the existing "adapthub_*" convention so previously stored values
 * can be migrated or read by older code without collisions.
 */
const NAMESPACE = 'adapthub_filters';

/**
 * SSR-safe read from localStorage. Returns null if unavailable or unparsable.
 *
 * @param key - Fully qualified storage key.
 */
const safeGet = (key: string): unknown | null => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null || raw === undefined) return null;
        return JSON.parse(raw) as unknown;
    } catch {
        return null;
    }
};

/**
 * SSR-safe write to localStorage. Silently ignores quota or serialization errors.
 *
 * @param key - Fully qualified storage key.
 * @param value - Value to persist (will be JSON.stringify'd).
 */
const safeSet = (key: string, value: unknown): void => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore quota or serialization errors
    }
};

/**
 * SSR-safe removal from localStorage.
 *
 * @param key - Fully qualified storage key.
 */
const safeRemove = (key: string): void => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
};

/**
 * Build the full localStorage key for a given view/field combination.
 *
 * @param viewId - View identifier (e.g. "reservations", "traces").
 * @param field - Optional field name within the view.
 */
const buildKey = (viewId: string, field?: string): string => {
    const base = `${NAMESPACE}:${viewId}`;
    return field ? `${base}.${field}` : base;
};

/**
 * usePersistedState - Drop-in replacement for useState that mirrors its value
 * to localStorage. Designed for "remember the last used filter" UX patterns.
 *
 * Backed by a JSON-serialized value, so primitives, arrays and plain objects
 * are all supported. The third tuple element is a reset callback that wipes
 * the stored value and restores the default that was passed on the most
 * recent render (callers that want stable reset semantics should keep the
 * default value referentially stable, e.g. by declaring it module-level).
 *
 * @param viewId - View identifier (e.g. "reservations").
 * @param field - Field name within the view (e.g. "filterStatus").
 * @param defaultValue - Default value when no stored value exists.
 * @returns [value, setValue, resetValue]
 */
export function usePersistedState<T>(
    viewId: string,
    field: string,
    defaultValue: T,
): [T, Dispatch<SetStateAction<T>>, () => void] {
    if (!viewId || !field) {
        throw new Error('usePersistedState requires a non-empty viewId and field.');
    }
    const fullKey = buildKey(viewId, field);

    const [value, setValue] = useState<T>(() => {
        const saved = safeGet(fullKey);
        return saved === null || saved === undefined ? defaultValue : (saved as T);
    });

    useEffect(() => {
        // Defer localStorage writes to avoid blocking the main thread
        // during rapid state changes (e.g., filter updates on large views).
        // JSON.stringify on large objects can take 100ms+ and triggers
        // the browser's 'click' handler violation warning.
        const taskId = setTimeout(() => {
            if (value === undefined) {
                safeRemove(fullKey);
            } else {
                safeSet(fullKey, value);
            }
        }, 0);
        return () => clearTimeout(taskId);
    }, [fullKey, value]);

    const resetValue = () => {
        safeRemove(fullKey);
        setValue(defaultValue);
    };

    return [value, setValue, resetValue];
}

/** Return contract of the usePersistedFilters hook. */
export interface PersistedFiltersResult<T extends Record<string, unknown>> {
    values: T;
    setField: (field: string, value: unknown) => void;
    setValues: (updater: Partial<T> | ((prev: T) => Partial<T>)) => void;
    resetFilters: () => void;
}

/**
 * usePersistedFilters - Persists a group of filter values as a single
 * localStorage object. Prefer this for views that have many related
 * filter inputs (e.g. status, search, dates) that should reset together.
 *
 * Behavior:
 *   - First mount: merges stored values over the provided defaults.
 *   - On every change: writes the merged object back to localStorage.
 *   - setField(name, val): updates a single field.
 *   - setValues(updater): shallow-merges a partial object or applies a function.
 *   - resetFilters(): clears the entry and restores the captured defaults.
 *
 * @param viewId - View identifier used for namespacing.
 * @param defaults - Default filter values keyed by field name.
 *   The current render's defaults are captured by the reset callback.
 */
export function usePersistedFilters<T extends Record<string, unknown> = Record<string, unknown>>(
    viewId: string,
    defaults: T,
): PersistedFiltersResult<T> {
    if (!viewId) {
        throw new Error('usePersistedFilters requires a non-empty viewId.');
    }
    const key = buildKey(viewId);

    const [values, setValuesState] = useState<Record<string, unknown>>(() => {
        const saved = safeGet(key);
        if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
            return { ...defaults, ...(saved as Record<string, unknown>) };
        }
        return { ...defaults };
    });

    useEffect(() => {
        // Defer localStorage writes to avoid blocking the main thread.
        // See usePersistedState for rationale.
        const taskId = setTimeout(() => {
            safeSet(key, values);
        }, 0);
        return () => clearTimeout(taskId);
    }, [key, values]);

    const setField = (field: string, value: unknown): void => {
        setValuesState((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
    };

    const setValues = (updater: Partial<T> | ((prev: T) => Partial<T>)): void => {
        setValuesState((prev) => {
            const next = typeof updater === 'function' ? (updater as (p: T) => Partial<T>)(prev as T) : updater;
            if (!next || typeof next !== 'object') return prev;
            return { ...prev, ...next };
        });
    };

    const resetFilters = (): void => {
        safeRemove(key);
        setValuesState({ ...defaults });
    };

    return { values: values as T, setField, setValues, resetFilters };
}

/**
 * Parses a URL search param value. Handles JSON arrays (comma-separated)
 * and plain strings. Returns null when the param is absent.
 *
 * @param raw - Raw URL search param value.
 * @returns Parsed value (string | boolean | number | string[]) or null.
 */
function parseParamValue(raw: string | null | undefined): string | boolean | number | string[] | null {
    if (raw === null || raw === undefined) return null;
    // Arrays: comma-separated, e.g. ?status=offen,In+Bearbeitung
    if (raw.includes(',')) {
        return raw.split(',').map(decodeURIComponent);
    }
    const decoded = decodeURIComponent(raw);
    // Coerce boolean strings so ?followUps=false is actually false
    if (decoded === 'true') return true;
    if (decoded === 'false') return false;
    // Coerce numeric strings to numbers so ?page=2 returns 2 (not "2")
    // Without this, setCurrentPage(p => p + 1) does "1" + 1 = "11"
    if (/^-?\d+$/.test(decoded)) return Number(decoded);
    return decoded;
}

/**
 * Serializes a value for a URL search param. Arrays become comma-separated.
 *
 * @param value - The value to serialize.
 * @returns Encoded string or null for empty/undefined.
 */
function encodeParamValue(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) {
        if (value.length === 0) return null;
        return value.map(encodeURIComponent).join(',');
    }
    const s = String(value);
    return s === '' ? null : encodeURIComponent(s);
}

/** Options accepted by the react-router navigate call inside useFilterParam. */
interface NavigateOptions {
    replace?: boolean;
    state?: unknown;
}

/** Options for useFilterParam. */
export interface UseFilterParamOptions {
    /**
     * Ephemeral-on-mount mode (used by useDateFilterParam for date filters):
     * the filter ALWAYS starts at the mount-time defaultValue — a value left
     * over in the URL or localStorage from a previous session is ignored and
     * cleared. This keeps date ranges anchored to "today" instead of reviving
     * a stale range the user filtered days ago. Once the user changes the
     * value during the component's lifetime, normal URL/localStorage syncing
     * resumes (so in-session navigation still shares the filter).
     */
    ephemeralOnMount?: boolean;
}

/** Shared singleton options for the ephemeral-on-mount mode — a stable module
 * reference keeps the returned setters/resetter referentially stable (a fresh
 * object literal per call would make resetValue's useCallback re-run every
 * render and re-trigger dependent effects). */
const EPHEMERAL_ON_MOUNT: UseFilterParamOptions = Object.freeze({ ephemeralOnMount: true });

/**
 * Pending-URL chain for synchronous multi-param writes.
 *
 * Bug context (2026-09-19): AnalyticsView.setPreset calls setDateFrom() and
 * setDateTo() back-to-back. Each call → applySearchParams, which builds the
 * next URL from `searchParamsRef.current` — the router's params as of the last
 * render. The second call therefore chained from a STALE snapshot and re-set
 * dateFrom back to its old value ("clobbered" the first write): preset buttons
 * only updated the second date cell.
 *
 * Fix: the FIRST applySearchParams in a synchronous burst publishes its built
 * URL here; every subsequent call in the same burst (before the router
 * re-renders) chains from that pending URL instead of the stale snapshot. The
 * effect in useFilterParam clears it as soon as the router commits (or when a
 * built URL was identical, where the router bails without re-rendering).
 */
let pendingUrlParams: URLSearchParams | null = null;

/**
 * Normalizes a decoded filter value to the declared default's type.
 *
 * parseParamValue eagerly coerces digit-only strings to numbers and
 * 'true'/'false' to booleans (for ?page=2 / ?followUps=false params). That is
 * WRONG for string-default filters: a user typing a room number like "402"
 * into a search box gets the NUMBER 404 back, and every `.toLowerCase()` /
 * `.trim()` downstream throws ("fe.toLowerCase is not a function",
 * "a.trim is not a function" — Traces/Fundsachen crash, 2026-09-07).
 *
 * Contract (mirrors the array normalization below): the returned value always
 * matches the declared default's type. Numbers/booleans coerced out of a
 * string default are converted back with String(); unparseable garbage for a
 * number/boolean default falls back to the default itself (never NaN).
 */
function coerceToDefaultType(rawValue: unknown, defaultValue: unknown): unknown {
    if (rawValue === null || rawValue === undefined) return defaultValue;
    if (Array.isArray(defaultValue)) {
        return Array.isArray(rawValue) ? rawValue : [rawValue];
    }
    switch (typeof defaultValue) {
        case 'string':
            if (typeof rawValue === 'number' || typeof rawValue === 'boolean') return String(rawValue);
            return typeof rawValue === 'string' ? rawValue : defaultValue;
        case 'number': {
            if (typeof rawValue === 'number') return Number.isFinite(rawValue) ? rawValue : defaultValue;
            if (typeof rawValue === 'string' && rawValue.trim() !== '') {
                const n = Number(rawValue);
                if (Number.isFinite(n)) return n;
            }
            return defaultValue;
        }
        case 'boolean':
            if (typeof rawValue === 'boolean') return rawValue;
            if (rawValue === 'true') return true;
            if (rawValue === 'false') return false;
            return defaultValue;
        default:
            // null / object defaults: no declared primitive type — passthrough
            return rawValue;
    }
}

/**
 * useFilterParam — a filter state that syncs between URL search params
 * (primary source — shareable, bookmarkable) and localStorage (fallback for
 * views not yet navigated via URL).
 *
 * Priorities on mount:
 *   1. URL search param (if present) → wins
 *   2. localStorage (usePersistedState) → fallback
 *   3. defaultValue → final fallback
 *
 * On change: writes to both URL (replace, no history spam) and localStorage.
 *
 * Type contract: the returned value ALWAYS matches the declared default's
 * type (see coerceToDefaultType). Array defaults → always an array; string
 * defaults → always a string (never a coerced number/boolean); number
 * defaults → always a finite number; boolean defaults → always a boolean.
 * This lets callers rely on .length / .includes() / .toLowerCase() / .trim()
 * regardless of what a URL param or stale localStorage entry contains.
 *
 * @param viewId - View identifier for localStorage namespacing.
 * @param field - Field name for both url param and localStorage key.
 * @param defaultValue - Default value when no source has a value.
 * @returns [value, setValue, resetValue]
 */
export function useFilterParam<T>(
    viewId: string,
    field: string,
    defaultValue: T,
    options?: UseFilterParamOptions,
): [T, (newValue: T | ((prev: T) => T)) => void, () => void] {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Keep the latest search params in a ref so the param-mutation helper below
    // stays referentially stable (handlers/effects depend on its identity).
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    /**
     * Mutates the URL search params while PRESERVING the URL hash fragment.
     * setSearchParams() navigates to "?<params>", which strips the hash and
     * breaks hash-based deep links (e.g. /offer/:id#token=..., apaleo_login).
     * Navigating to "?<params>#<hash>" keeps both intact.
     */
    const applySearchParams = useCallback((mutate: (prev: URLSearchParams) => URLSearchParams, options?: NavigateOptions) => {
        // Chain from the pending URL when a previous synchronous write already
        // built one (see pendingUrlParams docs) — otherwise the stale router
        // snapshot reverts the first write of a burst.
        const next = mutate(new URLSearchParams(pendingUrlParams ?? searchParamsRef.current));
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        const currentSearch = searchParamsRef.current.toString();
        if (next.toString() === currentSearch) {
            // Router will NOT re-render for an identical URL — clear any stale
            // chain so the next burst starts from the real snapshot again.
            pendingUrlParams = null;
        } else {
            pendingUrlParams = next;
        }
        navigate(`?${next.toString()}${hash}`, options);
    }, [navigate]);

    // Flush the pending chain as soon as the router catches up (or never
    // changed). Runs after every commit where search params re-identify.
    useEffect(() => {
        pendingUrlParams = null;
    }, [searchParams]);

    // Read localStorage-backed persisted state (fallback)
    // Read localStorage-backed persisted state (fallback)
    const [persisted, setPersisted, resetPersisted] = usePersistedState<T>(viewId, field, defaultValue);

    // Ephemeral-on-mount gate: while true, the effective value is the mount-time
    // default (fresh "today" for date filters) and stored copies are ignored.
    // Flipped to false the first time the caller sets a value in this mount's
    // lifetime, after which normal URL/localStorage syncing resumes. A ref is
    // used (not state) so the flip does not trigger its own re-render.
    const isFreshMountRef = useRef(options?.ephemeralOnMount === true);

    // Determine the effective value: URL param wins, else localStorage.
    // Memoize the parsed URL value on the raw string so a comma-separated param
    // (e.g. ?properties=SIEGEN,BERLIN) does NOT produce a fresh array reference on
    // every render — that would make the returned value unstable and re-trigger
    // downstream effects in an infinite loop.
    const urlRaw = searchParams.get(field);
    const urlValue = useMemo(() => urlRaw !== null ? parseParamValue(urlRaw) : null, [urlRaw]);
    const rawValue: unknown = isFreshMountRef.current
        ? null
        : (urlValue !== null ? urlValue : persisted);
    // Capture the initial default in a ref so the memoized value does NOT depend
    // on the (potentially unstable) defaultValue reference. Callers frequently pass
    // inline arrays/objects (e.g. `... : []`), which would otherwise produce a new
    // value reference on every render and trigger infinite effect loops downstream
    // (e.g. BuchhaltungView's fetchAccountsSchema loop hitting Apaleo 429).
    const defaultRef = useRef(defaultValue);

    // Normalize: the returned value ALWAYS matches the declared default's type.
    // Array defaults → always an array (parseParamValue returns a bare string
    // for single-value params, e.g. "InHouse" instead of ["InHouse"]).
    // String defaults → always a string (parseParamValue coerces "402" to the
    // number 402, which crashed .toLowerCase()/.trim() consumers). See
    // coerceToDefaultType for the full contract.
    const value = useMemo<T>(
        () => coerceToDefaultType(rawValue, defaultRef.current) as T,
        [rawValue]
    );

    // Keep the latest value in a ref so setValue can resolve functional updates
    // against the current value WITHOUT depending on it. This keeps setValue
    // referentially stable across renders — otherwise any effect that lists
    // setValue in its deps (e.g. BuchhaltungView's property-init effect) would
    // re-run on every value change and clobber the user's selection.
    const valueRef = useRef<T>(value);
    valueRef.current = value;

    // Sync URL ← localStorage on mount when URL has no param
    useEffect(() => {
        if (isFreshMountRef.current) {
            // Ephemeral date filter: discard whatever URL/localStorage carried
            // over from a previous session, then persist the fresh default so
            // the value survives an in-session re-render without a URL param.
            resetPersisted();
            applySearchParams(prev => {
                prev.delete(field);
                return prev;
            }, { replace: true });
            return;
        }
        if (urlRaw === null) {
            const encoded = encodeParamValue(persisted);
            if (encoded !== null) {
                applySearchParams(prev => {
                    prev.set(field, encoded);
                    return prev;
                }, { replace: true });
            }
        }
        // Only run on mount — we don't want to fight the URL
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setValue = useCallback((newValue: T | ((prev: T) => T)) => {
        // Leaving ephemeral mount mode: from here on, normal URL/localStorage
        // syncing applies (the user has actively chosen a value).
        if (isFreshMountRef.current) {
            isFreshMountRef.current = false;
        }
        // Support functional updates (setX(prev => ...)) by resolving against the
        // current effective value, mirroring React's useState setter contract.
        // Without this, a function passed as the value gets stringified into the
        // URL (e.g. quickFilters becomes ["(prev) => {...}"]) and corrupts state.
        const resolved = typeof newValue === 'function' ? (newValue as (prev: T) => T)(valueRef.current) : newValue;
        setPersisted(resolved);
        applySearchParams(prev => {
            const encoded = encodeParamValue(resolved);
            if (encoded === null) {
                prev.delete(field);
            } else {
                prev.set(field, encoded);
            }
            return prev;
        });
    }, [field, setPersisted, applySearchParams]);

    const resetValue = useCallback(() => {
        // Restore fresh-mount semantics: the reset value becomes the anchor
        // again (stale copies are gone, nothing to revive until the user
        // actively changes the filter).
        isFreshMountRef.current = options?.ephemeralOnMount === true;
        resetPersisted();
        applySearchParams(prev => {
            prev.delete(field);
            return prev;
        }, { replace: true });
    }, [field, resetPersisted, applySearchParams, options]);

    return [value, setValue, resetValue];
}

/**
 * useDateFilterParam — a date-range filter (or any filter that should anchor
 * to the CURRENT date) that resets to its mount-time default whenever the
 * owning component mounts. The mount-time default is computed fresh at call
 * time (e.g. `localToday()`), so a dashboard opened on Monday shows Monday's
 * date range, never last Friday's remembered range.
 *
 * Semantics (identical to useFilterParam with { ephemeralOnMount: true }):
 *   - On mount: ignores AND clears URL param + localStorage fallback.
 *   - During the session: value syncs to URL/localStorage as usual, so
 *     navigating away and back within the same session keeps the filter.
 *   - After a full reload / next session: back to the fresh default.
 *
 * For paired from/to ranges, pass the range's fresh defaults (e.g. the Sales
 * view's `toLocalIsoOffset(new Date(), -30)` / `localToday()` pair) — the
 * "reset to today" behavior means the range recomputes around today, not that
 * from and to both collapse to today's single date.
 *
 * @param viewId - View identifier for localStorage namespacing.
 * @param field - Field name for both url param and localStorage key.
 * @param defaultValue - Fresh default computed at mount time (call localToday()
 *   or equivalent inline — the hook captures it via ref on first render).
 * @returns [value, setValue, resetValue]
 */
export function useDateFilterParam<T>(
    viewId: string,
    field: string,
    defaultValue: T,
): [T, (newValue: T | ((prev: T) => T)) => void, () => void] {
    return useFilterParam<T>(viewId, field, defaultValue, EPHEMERAL_ON_MOUNT);
}
